import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { tracking } from '@iwoca/frontend-tracking-library';
import { i18n } from '@lingui/core';
import TagManager from 'react-gtm-module';
import { isIwocaDE } from 'utils/countryHelper';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { DE_LOCALE, UK_LOCALE } from 'utils/i18n';
import { oneTrustCookies } from 'utils/oneTrustCookies.js';
import getRuntimeEnvironment from 'utils/runtimeEnvironment';
import { messages as deMessages } from '../locales/de/messages';
import { messages as enMessages } from '../locales/en-GB/messages';

export const initializeDataDog = () => {
  const isProductionBuild =
    import.meta.env.MODE === 'production' &&
    !import.meta.env.VITE_PUBLIC_STORYBOOK_BUILD;

  if (isProductionBuild) {
    datadogLogs.init({
      clientToken:
        getRuntimeEnvironment() === 'production'
          ? 'pubd4d44f118d22163983e204126e748dfd'
          : 'puba4c457086288b431150f843896d21939',
      site: 'datadoghq.com',
      service: 'introducer_portal',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env: getRuntimeEnvironment(),
      version: import.meta.env.REACT_APP_RELEASE,
    });

    datadogRum.init({
      applicationId:
        getRuntimeEnvironment() === 'production'
          ? 'aba9cda9-8804-46fc-8827-f5c4608c7acc'
          : '53155a74-e1a4-4098-8ace-0f2cc58e7706',
      clientToken:
        getRuntimeEnvironment() === 'production'
          ? 'pubf1e7d967f260e8d19b3d75368ec583fb'
          : 'pub9836fd2e625754d82b9da10ef17562a3',
      site: 'datadoghq.com',
      service: 'introducer-portal',
      env: isIwocaDE() ? 'de' : 'en',
      version: import.meta.env.REACT_APP_RELEASE,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // `mask-user-input` masks every <input>, <select>, and <textarea> value
      // for Datadog Session Replay across the app. Form controls therefore
      // only need the Fullstory `fs-mask` class (use PRIVACY_FIELD_CLASS from
      // src/utils/privacyClasses.ts). For non-input PII display elements,
      // use PRIVACY_DISPLAY_CLASSES from the same file.
      defaultPrivacyLevel: 'mask-user-input',
    });
  }
};

export const initialiseTracking = () => {
  const runtimeEnvironment = getRuntimeEnvironment();
  if (typeof window !== 'undefined') {
    tracking.initialise({
      environment:
        runtimeEnvironment === 'production' ? 'production' : 'development',
    });
  }
};

export const initializeLingui = () => {
  const locale = isIwocaDE() ? DE_LOCALE : UK_LOCALE;
  i18n.load({
    [UK_LOCALE]: enMessages,
    [DE_LOCALE]: deMessages,
  });
  i18n.activate(locale);
};

const initializeGoogleTagManager = () => {
  if (getRuntimeEnvironment() !== 'production') return;

  function getGTMID() {
    const host = window.location.host;
    const isDE = host && host.indexOf('.de') !== -1;
    if (isDE) return 'GTM-PN3DHT';
    return 'GTM-KH9323';
  }

  TagManager.initialize({ gtmId: getGTMID() });
};

export const loadOneTrustScripts = () => {
  const locale = isIwocaDE() ? DE_LOCALE : UK_LOCALE;
  const oneTrustDomainScript = oneTrustCookies(getRuntimeEnvironment(), locale);

  const autoBlockScript = document.createElement('script');
  autoBlockScript.src = `https://cdn-ukwest.onetrust.com/consent/${oneTrustDomainScript}/OtAutoBlock.js`;
  autoBlockScript.type = 'text/javascript';
  document.head.appendChild(autoBlockScript);

  const sdkStubScript = document.createElement('script');
  sdkStubScript.src =
    'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js';
  sdkStubScript.type = 'text/javascript';
  sdkStubScript.setAttribute('data-domain-script', oneTrustDomainScript);
  sdkStubScript.onload = () => {
    (window as any).OptanonWrapper = function () {};
    (window as any).OptanonWrapper();
    initializeGoogleTagManager();
  };
  document.head.appendChild(sdkStubScript);
};

export const setLangAttribute = () => {
  document.documentElement.lang = isIwocaDE() ? 'de' : 'en';
};

export const getBasename = () => {
  if (getEnvironment() === 'development') {
    return undefined;
  }
  return isIwocaDE() ? '/partner/portal' : '/introducer/portal';
};
